import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1StoresStoreIdWebhooksIdBody,
  PostAdminV1StoresStoreIdWebhooksBody,
  WebhookEndpoint,
  WebhookEndpointWithSecret,
} from '@nucleus-commerce/api-client/admin';

import { webhooksApi } from '../api/webhooks-api';

import { webhookKeys } from './webhook-keys';

/** Caches the endpoint without its secret, so the secret cannot resurface on another screen. */
function withoutSecret({
  secret: _secret,
  ...endpoint
}: WebhookEndpointWithSecret): WebhookEndpoint {
  return endpoint;
}

function remember(
  queryClient: ReturnType<typeof useQueryClient>,
  storeId: string,
  endpoint: WebhookEndpoint,
) {
  queryClient.setQueryData(webhookKeys.detail(storeId, endpoint.id), endpoint);
  return queryClient.invalidateQueries({ queryKey: webhookKeys.list(storeId) });
}

/** 422 on `url` (https outside localhost) or `events`; LIMIT_EXCEEDED past 20 endpoints. */
export function useCreateWebhook(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdWebhooksBody) => webhooksApi.create(storeId, body),
    meta: { successMessage: 'webhooks:toasts.created', silentError: true },
    onSuccess: (endpoint) => remember(queryClient, storeId, withoutSecret(endpoint)),
  });
}

/** Re-enabling (`status: active`) also resets the failure count. */
export function useUpdateWebhook(storeId: string, successMessage = 'webhooks:toasts.updated') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdWebhooksIdBody }) =>
      webhooksApi.update(storeId, id, body),
    meta: { successMessage, silentError: true },
    onSuccess: (endpoint) => remember(queryClient, storeId, endpoint),
  });
}

export function useDeleteWebhook(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => webhooksApi.remove(storeId, id),
    meta: { successMessage: 'webhooks:toasts.deleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: webhookKeys.detail(storeId, id) });
      return queryClient.invalidateQueries({ queryKey: webhookKeys.list(storeId) });
    },
  });
}

/** The old secret stops working at once; the new one is in this response only. */
export function useRotateWebhookSecret(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => webhooksApi.rotateSecret(storeId, id),
    meta: { successMessage: 'webhooks:toasts.rotated', silentError: true },
    onSuccess: (endpoint) => remember(queryClient, storeId, withoutSecret(endpoint)),
  });
}

/** 409 while the endpoint is disabled. */
export function useSendTestWebhook(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => webhooksApi.test(storeId, id),
    meta: { successMessage: 'webhooks:toasts.testQueued', silentError: true },
    onSuccess: (delivery) =>
      queryClient.invalidateQueries({
        queryKey: webhookKeys.deliveries(storeId, delivery.endpointId),
      }),
  });
}

/** Sends a past delivery again as a new delivery row. */
export function useRedeliverWebhook(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, deliveryId }: { id: string; deliveryId: string }) =>
      webhooksApi.redeliver(storeId, id, deliveryId),
    meta: { successMessage: 'webhooks:toasts.redelivered', silentError: true },
    onSuccess: (delivery) =>
      queryClient.invalidateQueries({
        queryKey: webhookKeys.deliveries(storeId, delivery.endpointId),
      }),
  });
}
